import { collection, doc, getDoc, getDocs, query, where, type DocumentData } from 'firebase/firestore';
import { db } from '../config';
import { orgGroupNames, type GroupLike } from '@/lib/groups/orgGroupNames';
import type { CustomerSectionInfo } from './customer-section';
import { CHUNK_CONCURRENCY, chunkIds, forEachLimited, getDocsByIds } from './batchedIn';

/**
 * Names of the support groups a customer belongs to within one organization.
 * Membership lives in `groupMembers` (userId → groupId); the group itself in
 * `supportGroups`. Mirrors customer-section.ts: never throws — a lookup
 * failure renders as "no group", the same as an empty membership.
 */
export async function getCustomerGroupNames(userId: string, organizationId: string): Promise<string[]> {
  try {
    const memberships = await getDocs(query(collection(db, 'groupMembers'), where('userId', '==', userId)));
    const groupIds = Array.from(
      new Set(memberships.docs.map((d) => d.data().groupId as string).filter(Boolean)),
    );
    if (groupIds.length === 0) return [];

    const groups = await Promise.all(
      groupIds.map(async (gid): Promise<GroupLike | null> => {
        const snap = await getDoc(doc(db, 'supportGroups', gid));
        if (!snap.exists()) return null;
        const data = snap.data() as any;
        return { name: data.name, organization: data.organization, groupType: data.groupType };
      }),
    );
    return orgGroupNames(groups.filter((g): g is GroupLike => g !== null), organizationId);
  } catch (error) {
    console.error('Error getting customer group names:', error);
    return [];
  }
}

/** One customer's section (getCustomerSectionInfo) and group names (getCustomerGroupNames). */
export interface CustomerGrouping {
  section: CustomerSectionInfo | null;
  groupNames: string[];
}

/**
 * Pure: getCustomerSectionInfo over already-read rows. `firstGroupId` is the
 * group of the customer's first membership; `groups` holds every supportGroups
 * doc that exists among the ones read.
 */
export function sectionOf(
  firstGroupId: string | undefined,
  groups: Map<string, DocumentData>,
  organizationId: string,
): CustomerSectionInfo | null {
  const groupData = firstGroupId ? groups.get(firstGroupId) : undefined;
  if (!firstGroupId || !groupData) return null;
  // Check if this group belongs to the organization
  if (groupData.organization !== organizationId) return null;
  // A parent group (section) is its own section
  if (groupData.groupType === 'parent') {
    return { sectionId: firstGroupId, sectionName: groupData.name, groupId: firstGroupId, groupName: groupData.name };
  }
  // A child group's section is its parent, when that still exists
  const parent = groupData.parentGroupId ? groups.get(groupData.parentGroupId) : undefined;
  if (parent) {
    return { sectionId: groupData.parentGroupId, sectionName: parent.name, groupId: firstGroupId, groupName: groupData.name };
  }
  // If no parent found, return the group itself as the section
  return { sectionId: firstGroupId, sectionName: groupData.name, groupId: firstGroupId, groupName: groupData.name };
}

/**
 * getCustomerSectionInfo and getCustomerGroupNames for a whole roster (#117):
 * groupMembers with `userId in` (30 per query), then the groups they name and
 * the sections above those with `documentId() in`, instead of 4+ reads per
 * customer. Never throws: like the per-customer reads, a customer whose rows
 * failed to load gets no section and no groups.
 */
export async function getCustomersSectionsAndGroups(
  userIds: string[],
  organizationId: string,
): Promise<Map<string, CustomerGrouping>> {
  // Membership group ids per user, in the order the per-user query returns them.
  const memberships = new Map<string, string[]>();
  const failedUsers = new Set<string>();
  await forEachLimited(chunkIds(userIds), CHUNK_CONCURRENCY, async (part) => {
    try {
      const snap = await getDocs(query(collection(db, 'groupMembers'), where('userId', 'in', part)));
      part.forEach((uid) => memberships.set(uid, []));
      snap.docs.forEach((d) => memberships.get(d.data().userId)?.push(d.data().groupId));
    } catch (error) {
      console.error('Error getting group memberships for a roster chunk:', error);
      part.forEach((uid) => failedUsers.add(uid));
    }
  });

  const failedGroups = new Set<string>();
  const onGroupsError = (ids: string[], error: unknown) => {
    console.error('Error getting support groups for a roster chunk:', error);
    ids.forEach((id) => failedGroups.add(id));
  };
  const groupIds = Array.from(memberships.values()).flat().filter(Boolean);
  const groups = await getDocsByIds('supportGroups', groupIds, onGroupsError);
  // Sections above the first memberships' child groups, when not read already.
  const parentIds = Array.from(memberships.values())
    .map((ids) => (ids[0] ? groups.get(ids[0]) : undefined))
    .filter((g) => g && g.organization === organizationId && g.groupType !== 'parent' && g.parentGroupId)
    .map((g) => g!.parentGroupId as string)
    .filter((id) => !groups.has(id) && !failedGroups.has(id));
  (await getDocsByIds('supportGroups', parentIds, onGroupsError)).forEach((data, id) => groups.set(id, data));

  const out = new Map<string, CustomerGrouping>();
  for (const uid of chunkIds(userIds).flat()) {
    const ids = memberships.get(uid);
    if (!ids || failedUsers.has(uid)) {
      out.set(uid, { section: null, groupNames: [] });
      continue;
    }
    const distinct = Array.from(new Set(ids.filter(Boolean)));
    const first = groups.get(ids[0]);
    const sectionFailed =
      failedGroups.has(ids[0]) ||
      (!!first?.parentGroupId && first.groupType !== 'parent' && first.organization === organizationId &&
        failedGroups.has(first.parentGroupId));
    out.set(uid, {
      section: sectionFailed ? null : sectionOf(ids[0], groups, organizationId),
      groupNames: distinct.some((gid) => failedGroups.has(gid))
        ? []
        : orgGroupNames(
            distinct
              .map((gid) => groups.get(gid))
              .filter((g): g is DocumentData => g !== undefined)
              .map((data) => ({ name: data.name, organization: data.organization, groupType: data.groupType })),
            organizationId,
          ),
    });
  }
  return out;
}
